<template>
  <div>
    <preloader :preloaderStatus="!loaded" ></preloader>

  <div class="vendor-trends-item-wrapper dashboards-rating-map">

      <div class="dashboards-filters">

        <h4>{{ title }}</h4>

        <div class="dashboards-filters-item dashboards-filters-date date-simple">
          <date-picker
            v-model="params.creation_date"
            format="D MMM"
            range-separator="-"
            :editable="false"
            :shortcuts="false"
            input-class="date-simple-input"
            range
            lang="en"
            class="dark"
            :disabled="params.country === null"
          ></date-picker>
        </div>

        <div class="dashboards-filters-item">
          <filter-multiselect
            v-model="params.country"
            :options="countriesList"
            field-name="CountryName"
            placeholder="Country"
            :multiple="false"
            required
          />
        </div>

        <div class="dashboards-filters-item">
          <language-picker
            :options="languagesList"
            v-model="params.sourceLanguage"
            placeholder="Source language"
            :disabled="params.country === null"
          ></language-picker>
        </div>

        <div class="dashboards-filters-item">
          <language-picker
            :options="languagesList"
            v-model="params.targetLanguage"
            placeholder="Target language"
            :disabled="params.country === null"
          ></language-picker>
        </div>

        <div class="dashboards-filters-item">
          <filter-multiselect
            v-model="params.industry_field"
            :options="industryFieldList"
            field-name="Name"
            placeholder="Industry sector(s)"
            :disabled="params.country === null"
          />
        </div>

        <div class="dashboards-filters-item">
          <filter-multiselect
            v-model="params.doc_type"
            :options="textCategoryList"
            field-name="Name"
            placeholder="Document type(s)"
            :disabled="params.country === null"
          />
        </div>

        <div class="dashboards-filters-item">
          <filter-multiselect
            v-model="params.qaModel"
            :options="qaModelsList"
            field-name="Name"
            placeholder="QA model(s)"
            :disabled="params.country === null"
          />
        </div>

        <div class="dashboards-filters-item">
          <filter-multiselect
            v-model="params.qaMethod"
            :options="qaMethodsList"
            field-name="Name"
            placeholder="Method(s)"
            :disabled="params.country === null"
          />
        </div>
      </div>
      <template>
        <chart-constructor
          :options="chartOptions"
          @loaded="toggleLoaded()"
        />
      </template>
    </div>

  </div>

</template>

<script>
import Vue from 'vue';
import props from './props';
import Api from '@/utils/api';

export default Vue.extend({
  name: 'CountryKpiMainItem',
  components: {
    'language-picker': () =>
      import('@/components/selectors/language/index.vue'),
    'filter-multiselect': () =>
      import('@/components/selectors/filterMultiple/index.vue'),
    'date-picker': () => import('vue2-datepicker'),
    'chart-constructor': () =>
      import('@/components/charts/chartConstructor/index.js'),
    'preloader': () =>
      import('@/components/preloader'),
  },
  props: {
    ...props,
  },
  data: () => ({
    chartOptions: Object(),
    loaded: false,
    chartData: null,
  }),
  watch: {
    value: {
      handler() {
        this.redrawGraph();
      },
      deep: true,
    },
  },
  computed: {
    params: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    ajaxParams() {
      return {
        CountryIsoCode: this.params.country ? this.params.country.CountryIsoCode : null,
        SelectedDates: this.params.creation_date && this.params.creation_date.length > 0 ? this.convertDate() : null,
        SourceLang: this.params.sourceLanguage && this.params.sourceLanguage.ID
          ? [this.params.sourceLanguage.ID] : null,
        TargetLang: this.params.targetLanguage && this.params.targetLanguage.ID
          ? [this.params.targetLanguage.ID] : null,
        IndustrySectors: this.params.industry_field.map((el) => el.ID),
        DocumentTypes: this.params.doc_type.map((el) => el.ID),
        QaModelIds: this.params.qaModel.map((el) => el.ID),
        EvMethodIds: this.params.qaMethod.map((el) => el.ID),
      };
    },
  },
  methods: {
    convertDate() {
      return this.params.creation_date.includes(null) ? null : this.params.creation_date.map((date) => Date.parse(date) / 1000);
    },
    toggleLoaded() {
      this.loaded = !this.loaded;
    },
    genOptionsObject(series) {
      const categories = series.map((e) => e.name);
      return {
        chart: {
          type: 'column',
          height: 200,
        },
        title: {
          text: '',
        },
        xAxis: {
          opposite: true,
          categories,
        },
        yAxis: {
          visible: false,
        },
        plotOptions: {
          column: {
            color: '#434348',
            stacking: 'normal',
            dataLabels: {
              enabled: true,
              formatter: function() {
                return this.point.data;
              },
              verticalAlign: 'center',
              style: {
                fontSize: '60px',
              },
            },
          },
        },
        tooltip: {
          formatter: function() {
            return `${this.x}: <b>${this.point.data}</b>`;
          },
        },
        legend: {
          enabled: false,
        },
        series,
      };
    },
    async redrawGraph() {
      if (this.value.country ||
        !!this.value.creation_date.length ||
        !!this.value.industry_field.length ||
        !!this.value.doc_type.length ||
        this.value.sourceLanguage || this.value.targetLanguage ||
        !!this.value.qaModel.length || !!this.value.qaMethod.length) {
        //
        await this.getChartData();
        const series = await this.getData();
        this.chartOptions = this.genOptionsObject(series);
      } else {
        this.chartOptions = this.genOptionsObject(Array());
      }
    },
    getChartData() {
      return new Promise((resolve) => {
        Api.dashboard.getCountriesKpi.language(this.ajaxParams).then((resp) => {
          if (resp.data.Data) {
            this.chartData = resp.data.Data;
            return resolve();
          }
        });
      }
      );
    },
    // implement method in child
    getData() {
      return new Promise((res) => {
        res(Array());
      });
    },
  },
  mounted() {
    this.redrawGraph();
  },
});
</script>

<style lang="scss">
.vendor-trends-item-wrapper {
  margin-bottom: 50px;
  background: #fff;
}
.dashboards-rating-map {
  .dashboards-filters {
    flex-flow: row wrap;
    margin-bottom: 0;
    height: auto;
    // display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 7px;
    padding: 5px 2vw 25px 2vw;

    &-heading {
      padding: 12px;
      text-align: center;
    }

    &-date {
      margin-left: 40px;
    }

    &-item {
      .multiselect {
        white-space: nowrap;
        cursor: pointer;
        &--disabled {
          cursor: not-allowed !important;
        }
      }

      .filter-multiselect.required::before {
              left: unset !important;
              right: 0 !important;
      }

      .lang-selector {
        width: unset;
      }
    }

  &-date {
    margin-left: 40px;
  }
}

}

</style>
